import React, { useCallback, useEffect, useState } from "react";
import api, { describeAxiosError } from "@/lib/api";
import { toast } from "sonner";
import PartnerLayout from "./PartnerLayout";
import {
    MagnifyingGlass,
    MapPin,
    Briefcase,
    Building,
    PaperPlaneTilt,
    Paperclip as PaperclipIcon,
    Check,
    X as CloseIcon,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const dt = new Date(iso);
        const days = Math.floor((Date.now() - dt.getTime()) / 86400000);
        if (days < 1) return "today";
        if (days < 30) return `${days}d ago`;
        return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return ""; }
};

const fmtCTC = (min, max) => {
    if (!min && !max) return null;
    if (min && max) return `₹${min}L – ₹${max}L`;
    return `₹${min || max}L`;
};

function SubmitModal({ job, onClose, onSuccess }) {
    const [form, setForm] = useState({
        candidate_name: "",
        candidate_email: "",
        candidate_phone: "",
        candidate_experience_years: "",
        candidate_expected_ctc_lakhs: "",
        notice_period_days: "",
        notes: "",
    });
    const [cvFile, setCvFile] = useState(null);
    const [cvUploadId, setCvUploadId] = useState(null);
    const [cvUploading, setCvUploading] = useState(false);
    const [cvError, setCvError] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const uploadCv = async (file) => {
        setCvError("");
        setCvUploading(true);
        setCvUploadId(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/partner/me/uploads/cv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setCvUploadId(r.data.upload_id);
            toast.success("Resume attached");
        } catch (e) {
            setCvError(describeAxiosError(e));
            setCvFile(null);
        } finally {
            setCvUploading(false);
        }
    };

    const onPickCv = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // 4 MB cap matches backend
        if (f.size > 4 * 1024 * 1024) {
            setCvError("Resume is too big — max 4 MB.");
            e.target.value = "";
            return;
        }
        setCvFile(f);
        uploadCv(f);
    };

    const clearCv = () => {
        setCvFile(null);
        setCvUploadId(null);
        setCvError("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const payload = { ...form };
            // Coerce numbers
            ["candidate_experience_years", "candidate_expected_ctc_lakhs", "notice_period_days"].forEach((k) => {
                if (payload[k] === "" || payload[k] == null) delete payload[k];
                else payload[k] = Number(payload[k]);
            });
            if (cvUploadId) payload.cv_upload_id = cvUploadId;
            const r = await api.post(`/partner/me/submit/${job.id}`, payload);
            toast.success("Candidate submitted — recruiter has been notified");
            onSuccess(r.data);
        } catch (e) {
            setErr(describeAxiosError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            data-testid="partner-submit-modal"
        >
            <div
                className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-5 py-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0891B2]">Submit candidate</p>
                        <h2 className="font-heading font-black text-lg tracking-tight truncate">{job.title}</h2>
                        <p className="text-[11px] text-[#A3A3A3]">{job.agency_name}{job.location ? ` · ${job.location}` : ""}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="partner-submit-close"
                            className="text-[#A3A3A3] hover:text-[#0A0A0A] p-1 -m-1">
                        <CloseIcon size={20} weight="bold" />
                    </button>
                </div>

                <form onSubmit={submit} className="px-5 py-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                            Candidate name *
                        </label>
                        <input
                            required
                            type="text"
                            data-testid="partner-submit-name"
                            value={form.candidate_name}
                            onChange={(e) => setForm({ ...form, candidate_name: e.target.value })}
                            className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                Email *
                            </label>
                            <input
                                required
                                type="email"
                                data-testid="partner-submit-email"
                                value={form.candidate_email}
                                onChange={(e) => setForm({ ...form, candidate_email: e.target.value })}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                Phone *
                            </label>
                            <input
                                required
                                type="tel"
                                data-testid="partner-submit-phone"
                                value={form.candidate_phone}
                                onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                Exp (yrs)
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={60}
                                step={0.5}
                                data-testid="partner-submit-exp"
                                value={form.candidate_experience_years}
                                onChange={(e) => setForm({ ...form, candidate_experience_years: e.target.value })}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                Exp. CTC (L)
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={999}
                                step={0.5}
                                data-testid="partner-submit-ctc"
                                value={form.candidate_expected_ctc_lakhs}
                                onChange={(e) => setForm({ ...form, candidate_expected_ctc_lakhs: e.target.value })}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                Notice (d)
                            </label>
                            <input
                                type="number"
                                min={0}
                                max={365}
                                data-testid="partner-submit-notice"
                                value={form.notice_period_days}
                                onChange={(e) => setForm({ ...form, notice_period_days: e.target.value })}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                            Resume (PDF/DOCX · optional, ≤ 4 MB)
                        </label>
                        {!cvFile ? (
                            <label
                                data-testid="partner-submit-cv-picker"
                                className="flex items-center justify-center gap-2 border border-dashed border-[#E5E5E5] hover:border-[#0891B2] cursor-pointer py-4 text-xs text-[#525252] hover:text-[#0891B2] transition-colors"
                            >
                                <PaperclipIcon size={14} weight="bold" />
                                Attach resume
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={onPickCv}
                                    className="hidden"
                                    data-testid="partner-submit-cv-input"
                                />
                            </label>
                        ) : (
                            <div
                                data-testid="partner-submit-cv-attached"
                                className={`flex items-center justify-between gap-2 border px-3 py-2.5 text-xs ${
                                    cvUploading
                                        ? "border-[#E5E5E5] bg-[#FAFAFA] text-[#525252]"
                                        : "border-emerald-200 bg-emerald-50 text-emerald-900"
                                }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <PaperclipIcon size={12} weight="bold" className="shrink-0" />
                                    <span className="truncate">{cvFile.name}</span>
                                    <span className="text-[10px] text-[#A3A3A3] shrink-0">
                                        · {(cvFile.size / 1024).toFixed(0)} KB
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {cvUploading ? (
                                        <span className="text-[10px] font-black uppercase tracking-wider">Uploading…</span>
                                    ) : cvUploadId ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                                            <Check size={11} weight="bold" /> Attached
                                        </span>
                                    ) : null}
                                    <button
                                        type="button"
                                        onClick={clearCv}
                                        data-testid="partner-submit-cv-remove"
                                        className="text-[#A3A3A3] hover:text-[#DC2626] p-0.5"
                                        aria-label="Remove resume"
                                    >
                                        <CloseIcon size={12} weight="bold" />
                                    </button>
                                </div>
                            </div>
                        )}
                        {cvError && (
                            <p data-testid="partner-submit-cv-error"
                               className="text-[11px] text-[#DC2626] mt-1">{cvError}</p>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                            Note to recruiter
                        </label>
                        <textarea
                            rows={3}
                            data-testid="partner-submit-notes"
                            placeholder="Why is this candidate a strong fit? Any relevant context…"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            maxLength={1000}
                            className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2] resize-none"
                        />
                        <p className="text-[10px] text-[#A3A3A3] mt-1 text-right">{form.notes.length}/1000</p>
                    </div>

                    {err && (
                        <div data-testid="partner-submit-error"
                             className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                            {err}
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-2">
                        <button type="button" onClick={onClose}
                                className="flex-1 border border-[#E5E5E5] py-2.5 text-xs font-bold uppercase tracking-wider hover:border-[#0A0A0A]">
                            Cancel
                        </button>
                        <button type="submit" disabled={busy || cvUploading}
                                data-testid="partner-submit-confirm"
                                className="flex-1 bg-[#0891B2] hover:bg-[#0e7490] text-white py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center justify-center gap-1">
                            {busy ? "Submitting…" : cvUploading ? "Uploading resume…" : (<>
                                <PaperPlaneTilt size={12} weight="bold" /> Submit candidate
                            </>)}
                        </button>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] leading-relaxed">
                        First-submitter-wins ownership. If your candidate gets placed, the payout is credited to your partner account.
                    </p>
                </form>
            </div>
        </div>
    );
}

export default function PartnerMarketplace() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [q, setQ] = useState("");
    const [location, setLocation] = useState("");
    const [activeJob, setActiveJob] = useState(null);

    const PAGE_SIZE = 24;

    const load = useCallback(async (opts = {}) => {
        const append = !!opts.append;
        if (append) setLoadingMore(true);
        else setLoading(true);
        try {
            const params = { limit: PAGE_SIZE };
            if (q.trim()) params.q = q.trim();
            if (location.trim()) params.location = location.trim();
            params.offset = append ? rows.length : 0;
            const r = await api.get("/partner/me/marketplace", { params });
            const items = r.data.items || [];
            setRows((prev) => (append ? [...prev, ...items] : items));
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(describeAxiosError(e));
        } finally {
            if (append) setLoadingMore(false);
            else setLoading(false);
        }
    }, [q, location, rows.length]);

    useEffect(() => { load(); }, []); // initial

    const handleSuccess = () => {
        setActiveJob(null);
        // Refetch first page to flip already_submitted flag
        setRows([]);
        load();
    };

    const canLoadMore = !loading && rows.length < total;

    return (
        <PartnerLayout>
            <div className="space-y-6" data-testid="partner-marketplace-page">
                <header>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0891B2]">Marketplace</p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A] mt-1">
                        {total > 0 ? `${total} live requirements` : "Open roles"}
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Every open role across every agency on NaukriDaddy. Pick one, submit your best candidate — the first submission wins ownership.
                    </p>
                </header>

                {/* Filters */}
                <form
                    onSubmit={(e) => { e.preventDefault(); setRows([]); load(); }}
                    className="bg-white border border-[#E5E5E5] p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3"
                >
                    <div className="relative">
                        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                        <input
                            type="text"
                            placeholder="Role, skill or keyword…"
                            data-testid="partner-market-q"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full border border-[#E5E5E5] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                        />
                    </div>
                    <div className="relative">
                        <MapPin size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                        <input
                            type="text"
                            placeholder="City (Delhi, Bengaluru…)"
                            data-testid="partner-market-loc"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="w-full border border-[#E5E5E5] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0891B2]"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="partner-market-search"
                        className="bg-[#0A0A0A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#0891B2] transition-colors"
                    >
                        Search
                    </button>
                </form>

                {/* Results */}
                {loading && (
                    <div className="text-sm text-[#A3A3A3]">Loading roles…</div>
                )}

                {!loading && rows.length === 0 && (
                    <div className="bg-white border border-[#E5E5E5] p-10 text-center">
                        <p className="text-sm text-[#525252]">
                            No open roles match your filter. Try removing the location filter or check back later.
                        </p>
                    </div>
                )}

                {!loading && rows.length > 0 && (
                    <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-market-grid">
                        {rows.map((j) => (
                            <div key={j.id}
                                 data-testid={`partner-job-card-${j.id}`}
                                 className="bg-white border border-[#E5E5E5] p-4 sm:p-5 flex flex-col hover:border-[#0A0A0A] transition-colors">
                                <div className="flex-1">
                                    <div className="flex items-start gap-2 mb-1">
                                        <h3 className="font-heading font-black text-base sm:text-lg tracking-tight text-[#0A0A0A] flex-1 min-w-0">
                                            {j.title}
                                        </h3>
                                        {j.already_submitted && (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 shrink-0">
                                                <Check size={10} weight="bold" /> Submitted
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-[#525252] inline-flex items-center gap-1">
                                        <Building size={11} weight="bold" /> {j.agency_name}
                                        {j.client?.name ? ` · ${j.client.name}` : ""}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                        {j.location && (
                                            <span className="inline-flex items-center gap-1 bg-[#F5F5F5] px-2 py-0.5">
                                                <MapPin size={10} weight="bold" /> {j.location}
                                            </span>
                                        )}
                                        {j.employment_type && (
                                            <span className="inline-flex items-center gap-1 bg-[#F5F5F5] px-2 py-0.5">
                                                <Briefcase size={10} weight="bold" /> {j.employment_type}
                                            </span>
                                        )}
                                        {fmtCTC(j.ctc_lakhs_min, j.ctc_lakhs_max) && (
                                            <span className="inline-flex items-center gap-1 bg-cyan-50 text-cyan-800 px-2 py-0.5 font-bold">
                                                {fmtCTC(j.ctc_lakhs_min, j.ctc_lakhs_max)}
                                            </span>
                                        )}
                                    </div>
                                    {j.description_teaser && (
                                        <p className="text-xs text-[#525252] mt-3 line-clamp-3 leading-relaxed">
                                            {j.description_teaser}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-4 pt-3 border-t border-[#F5F5F5] flex items-center justify-between gap-3">
                                    <span className="text-[10px] text-[#A3A3A3]">Posted {fmtDate(j.created_at)}</span>
                                    <button
                                        type="button"
                                        onClick={() => setActiveJob(j)}
                                        disabled={j.already_submitted}
                                        data-testid={`partner-submit-btn-${j.id}`}
                                        className="bg-[#0891B2] hover:bg-[#0e7490] disabled:bg-[#E5E5E5] disabled:text-[#A3A3A3] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                                    >
                                        <PaperPlaneTilt size={11} weight="bold" />
                                        {j.already_submitted ? "Submitted" : "Submit"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {canLoadMore && (
                        <div className="flex justify-center pt-2">
                            <button
                                type="button"
                                onClick={() => load({ append: true })}
                                disabled={loadingMore}
                                data-testid="partner-market-load-more"
                                className="inline-flex items-center gap-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                            >
                                {loadingMore
                                    ? "Loading…"
                                    : `Load more · ${total - rows.length} left`}
                            </button>
                        </div>
                    )}
                    </>
                )}

                {activeJob && (
                    <SubmitModal
                        job={activeJob}
                        onClose={() => setActiveJob(null)}
                        onSuccess={handleSuccess}
                    />
                )}
            </div>
        </PartnerLayout>
    );
}
